import {
  useMemo,
  useCallback,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import type { InterPageNavigationProps, InterPageNavigationRef } from "./types";

export const InterPageNavigation = forwardRef<
  InterPageNavigationRef,
  InterPageNavigationProps<any>
>(
  (
    {
      currentItemId,
      filteredItems,
      onNavigateToItem,
      getSortValue,
      sortDirection = "desc",
      labels = {
        previous: "Vorig",
        next: "Volgend",
      },
      showPosition = true,
      compact = false,
      className = "",
      disableKeyboardNav = false,
      icons,
      isLoading = false,
      hideWhenSingle = true,
    },
    ref,
  ) => {
    // Sort items based on getSortValue function
    const sortedItems = useMemo(() => {
      const items = [...(filteredItems as any)];

      if (!getSortValue) {
        // Default sorting - try createdAt first, then id
        return items.sort((a, b) => {
          let aValue, bValue;

          if ("createdAt" in a && "createdAt" in b) {
            aValue = new Date(a.createdAt || "").getTime();
            bValue = new Date(b.createdAt || "").getTime();
          } else {
            aValue = a.id;
            bValue = b.id;
          }

          if (sortDirection === "desc") {
            return bValue > aValue ? 1 : -1;
          } else {
            return aValue > bValue ? 1 : -1;
          }
        });
      }

      return items.sort((a, b) => {
        const aValue = getSortValue(a);
        const bValue = getSortValue(b);

        if (sortDirection === "desc") {
          return bValue > aValue ? 1 : -1;
        } else {
          return aValue > bValue ? 1 : -1;
        }
      });
    }, [filteredItems, getSortValue, sortDirection]);

    // Find current item index
    const currentIndex = useMemo(() => {
      return sortedItems.findIndex(
        (item) => String(item.id) === String(currentItemId),
      );
    }, [sortedItems, currentItemId]);

    // Navigation functions
    const goToPrevious = useCallback(() => {
      if (currentIndex > 0) {
        const previousItem = sortedItems[currentIndex - 1];
        onNavigateToItem(previousItem.id);
      }
    }, [currentIndex, sortedItems, onNavigateToItem]);

    const goToNext = useCallback(() => {
      if (currentIndex >= 0 && currentIndex < sortedItems.length - 1) {
        const nextItem = sortedItems[currentIndex + 1];
        onNavigateToItem(nextItem.id);
      }
    }, [currentIndex, sortedItems, onNavigateToItem]);

    // Expose methods via ref
    useImperativeHandle(
      ref,
      () => ({
        goToPrevious,
        goToNext,
        getCurrentPosition: () => currentIndex + 1,
        getTotalItems: () => sortedItems.length,
      }),
      [goToPrevious, goToNext, currentIndex, sortedItems.length],
    );

    // Keyboard navigation
    useEffect(() => {
      if (disableKeyboardNav) return;

      const handleKeyDown = (event: KeyboardEvent) => {
        // Only handle navigation if no input is focused and no modals are open
        const activeElement = document.activeElement;
        const isInputFocused =
          activeElement?.tagName === "INPUT" ||
          activeElement?.tagName === "TEXTAREA" ||
          (activeElement as HTMLElement)?.contentEditable === "true";

        const isModalOpen = document.querySelector('[role="dialog"]') !== null;

        if (isInputFocused || isModalOpen) {
          return;
        }

        switch (event.key) {
          case "ArrowLeft":
            event.preventDefault();
            goToPrevious();
            break;
          case "ArrowRight":
            event.preventDefault();
            goToNext();
            break;
        }
      };

      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }, [goToPrevious, goToNext, disableKeyboardNav]);

    // Early return conditions
    if (isLoading) {
      return (
        <div className={`flex items-center gap-2 ${className}`}>
          <div className="h-8 w-8 animate-pulse rounded-full bg-neutral-200" />
          {showPosition && (
            <div className="h-8 w-12 animate-pulse rounded-full bg-neutral-200" />
          )}
          <div className="h-8 w-8 animate-pulse rounded-full bg-neutral-200" />
        </div>
      );
    }

    if (
      sortedItems.length === 0 ||
      (hideWhenSingle && sortedItems.length === 1)
    ) {
      return null;
    }

    const hasPrevious = currentIndex > 0;
    const hasNext = currentIndex >= 0 && currentIndex < sortedItems.length - 1;
    const currentPosition = currentIndex + 1;
    const totalItems = sortedItems.length;

    const previousIcon = icons?.previous || (
      <CaretLeft size={compact ? 14 : 16} />
    );
    const nextIcon = icons?.next || <CaretRight size={compact ? 14 : 16} />;

    return (
      <div className={`flex items-center gap-2 ${className}`}>
        {/* Previous Button */}
        <button
          onClick={goToPrevious}
          disabled={!hasPrevious}
          className={`rounded-full p-1.5 text-white ${
            !hasPrevious
              ? "bg-moss-primary/50"
              : "bg-moss-primary hover:bg-moss-primary/90"
          }`}
          title={labels.previous}
        >
          {previousIcon}
        </button>

        {/* Position Indicator */}
        {showPosition && (
          <div className="border-neutral-primary/20 flex min-w-10 items-center justify-center rounded-full border bg-white p-1.5">
            <span
              className={`${compact ? "text-xs" : "text-sm"} text-neutral-primary font-medium whitespace-nowrap`}
            >
              {compact
                ? `${currentPosition}/${totalItems}`
                : `${currentPosition} van ${totalItems}`}
            </span>
          </div>
        )}

        {/* Next Button */}
        <button
          onClick={goToNext}
          disabled={!hasNext}
          className={`rounded-full p-1.5 text-white ${
            !hasNext
              ? "bg-moss-primary/50"
              : "bg-moss-primary hover:bg-moss-primary/90"
          }`}
          title={labels.next}
        >
          {nextIcon}
        </button>
      </div>
    );
  },
);

InterPageNavigation.displayName = "InterPageNavigation";
